import type { FrameStats } from '../Stage.js';

/**
 * Development read-out: frame rate and the renderer's own counters.
 *
 * Deliberately not the future HUD. Gameplay HUD is DOM in its own layer; this is
 * an instrument for answering the questions this phase has to answer — how many
 * draw calls, how many triangles, is the frame time stable — and it is trivially
 * removable.
 *
 * Frame rate is reported as a median over a one-second window rather than a mean.
 * A mean is dragged around by single long frames (a GC pause, a tab switch) and
 * gives a number that matches neither the typical nor the worst case; the median
 * plus the worst frame time describes both.
 */

export interface StatsSnapshot {
  readonly fps: number;
  readonly medianFrameMs: number;
  readonly worstFrameMs: number;
  readonly stats: FrameStats;
}

export class StatsOverlay {
  readonly element: HTMLDivElement;
  private readonly samples: number[] = [];
  private windowStart = 0;
  private latest: StatsSnapshot | null = null;

  constructor(parent: HTMLElement = document.body) {
    this.element = document.createElement('div');
    this.element.className = 'mandalizm-stats';
    parent.appendChild(this.element);
  }

  update(now: number, deltaMs: number, stats: FrameStats): void {
    this.samples.push(deltaMs);
    if (this.windowStart === 0) this.windowStart = now;
    if (now - this.windowStart < 500) return;

    const sorted = [...this.samples].sort((left, right) => left - right);
    const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
    const worst = sorted[sorted.length - 1] ?? 0;

    this.latest = {
      fps: median > 0 ? 1000 / median : 0,
      medianFrameMs: median,
      worstFrameMs: worst,
      stats,
    };
    this.samples.length = 0;
    this.windowStart = now;
    this.render(this.latest);
  }

  /** Last computed snapshot. Read by the automated check in the browser. */
  snapshot(): StatsSnapshot | null {
    return this.latest;
  }

  private render(snapshot: StatsSnapshot): void {
    const { stats } = snapshot;
    this.element.textContent = [
      `FPS        ${snapshot.fps.toFixed(1)}`,
      `frame      ${snapshot.medianFrameMs.toFixed(2)} ms  (worst ${snapshot.worstFrameMs.toFixed(2)})`,
      `draw scene ${stats.sceneCalls}`,
      `draw total ${stats.totalCalls}  (incl. post)`,
      `triangles  ${stats.triangles.toLocaleString()}`,
      `points     ${stats.points.toLocaleString()}`,
      `programs   ${stats.programs}`,
      `geometries ${stats.geometries}   textures ${stats.textures}`,
    ].join('\n');
  }

  dispose(): void {
    this.element.remove();
  }
}
